import React, { forwardRef, cloneElement, useState, useRef, useEffect, useImperativeHandle } from 'react';
import PropTypes from 'prop-types';
import { Manager, Reference, Popper } from 'react-popper';
import { propsUtil, KEYCODES } from '@asphalt-react/helper';
import { sendStyles } from '@asphalt-react/context';
import { tabbable } from 'tabbable';

function _extends() {
  _extends = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };
  return _extends.apply(this, arguments);
}

function styleInject(css, ref) {
  if ( ref === void 0 ) ref = {};
  var insertAt = ref.insertAt;

  if (!css || typeof document === 'undefined') { return; }

  var head = document.head || document.getElementsByTagName('head')[0];
  var style = document.createElement('style');
  style.type = 'text/css';

  if (insertAt === 'top') {
    if (head.firstChild) {
      head.insertBefore(style, head.firstChild);
    } else {
      head.appendChild(style);
    }
  } else {
    head.appendChild(style);
  }

  if (style.styleSheet) {
    style.styleSheet.cssText = css;
  } else {
    style.appendChild(document.createTextNode(css));
  }
}

var css_248z = ".Popover__jbNAV {\n  --popover-box-shadow: var(\n    --shadow-low,\n    0px 0px 10px 0px hsla(222, 42%, 6%, 0.1)\n  );\n\n  box-shadow: var(--popover-box-shadow);\n  z-index: 1;\n  line-height: 1;\n}\n";
var styles = {"Popover":"Popover__jbNAV"};
var stylesheet=".Popover__jbNAV {\n  --popover-box-shadow: var(\n    --shadow-low,\n    0px 0px 10px 0px hsla(222, 42%, 6%, 0.1)\n  );\n\n  box-shadow: var(--popover-box-shadow);\n  z-index: 1;\n  line-height: 1;\n}\n";
styleInject(css_248z);

const Popover$1 = /*#__PURE__*/forwardRef(({
  children,
  target,
  targetProps,
  targetRef,
  open,
  placement,
  skipBounds,
  ...props
}, innerRef) => {
  //Send styles to server
  sendStyles(stylesheet); // filtering out style and className

  /* eslint-disable-next-line react/prop-types */

  const {
    style,
    className,
    ...rest
  } = props;
  const {
    mergeRefs
  } = propsUtil;
  return /*#__PURE__*/React.createElement(Manager, null, /*#__PURE__*/React.createElement(Reference, null, ({
    ref
  }) => /*#__PURE__*/cloneElement(target, {
    ref: mergeRefs(ref, targetRef),
    "aria-haspopup": true,
    "aria-expanded": open,
    ...targetProps
  })), open && /*#__PURE__*/React.createElement(Popper, {
    placement: placement,
    outOfBoundaries: true,
    positionFixed: skipBounds
  }, ({
    ref,
    placement,
    style
  }) => /*#__PURE__*/React.createElement("div", _extends({
    role: "dialog",
    ref: mergeRefs(innerRef, ref),
    "data-placement": placement,
    style: style,
    className: styles.Popover
  }, rest), children)));
});
Popover$1.displayName = "PopoverLite";
Popover$1.propTypes = {
  /**
   * content for the Popover
   */
  children: PropTypes.node.isRequired,

  /**
   * a node which will be the target for popover
   */
  target: PropTypes.element.isRequired,

  /**
   * props or attributes to be passed to the target element
   */
  targetProps: PropTypes.object,

  /**
   * to control hide and show of popover
   */
  open: PropTypes.bool,

  /**
   * placement of the popover with respect to `target` node
   */
  placement: PropTypes.oneOf(["auto", "auto-start", "auto-end", "top", "top-start", "top-end", "bottom", "bottom-start", "bottom-end", "right", "right-start", "right-end", "left", "left-start", "left-end"]),

  /**
   * Use fixed positioning strategy
   */
  skipBounds: PropTypes.bool,

  /**
   * Target element ref
   */
  targetRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({
    current: PropTypes.any
  })])
};
Popover$1.defaultProps = {
  placement: "auto",
  open: false
};

/**
 * returns the first, last and total focussable elements inside the target element
 *
 * runs a query to find all the focussable elements
 *
 * @param {Object} element element in which you need to find focussable elements
 * @returns {Object}
 */

const firstAndlastFocusEls = element => {
  const focusEls = tabbable(element);
  const total = focusEls.length;
  return {
    firstEl: focusEls[0],
    lastEl: focusEls[total - 1],
    total
  };
};
/**
 * implements focus trap inside an element
 *
 * @param {Object} element element in which focus trap needs to be implemented
 * @param {Object} event React.syntheticEvent
 */

const focusTrap = (element, event) => {
  const {
    firstEl,
    lastEl
  } = firstAndlastFocusEls(element);
  /**
   * if `shift` key is pressed
   * and the activeElement is first focussable element
   * loop to the last foccussable element and set focus to it
   */

  if (event.shiftKey && document.activeElement === firstEl) {
    lastEl.focus();
    event.preventDefault();
  }

  if (!event.shiftKey && document.activeElement === lastEl) {
    /**
     * the activeElement is the last focussable element
     * loop to the first focussable element and set focus to it
     */
    firstEl.focus();
    event.preventDefault();
  }
};
/**
 * checks if the event is generated from the element pointed by the ref
 *
 * @param {function | Object} innerRef ref which points to the Popover
 * @param {Object} event React.syntheticEvent
 * @returns {boolean}
 */

const isTargetInsideRef = (ref, {
  target
}) => {
  /**
   * `addEventListener` gets attached to the element's instance on first render.
   * Let's say a Popover conatains a stateful component, having a sub-component with an element
   * which changes the state upon interaction. Once interacted, the sub-component re-renders creating a
   * new instance of the element. The event object from the `addEventListerner` is still attached
   * to the old instance, hence the `event.target` also refers to the same instance.
   * Since the old instance is not part of the DOM anymore, our `ref.current.contains(target)` will always be false.
   */
  if (document.body.contains(target)) {
    return ref && ref.current && ref.current.contains(target);
  }

  return true;
};

let nodeToFocus = null;
const Popover = /*#__PURE__*/forwardRef(({
  children,
  target,
  placement,
  onOpen,
  onClose,
  smartFocus,
  skipBounds,
  ...props
}, ref) => {
  if (!target) {
    console.error(`Popover: pass an element in the "target" prop`);
    return null;
  } // removing support for `style` and `className` attributes

  /* eslint-disable-next-line react/prop-types */


  const {
    style,
    className,
    ...rest
  } = props;
  const [visible, setVisible] = useState(false);
  const innerRef = useRef(null);
  const targetRef = useRef(null);
  /**
   * opens the Popover
   */

  const open = () => setVisible(true);
  /**
   * closes the Popover
   */


  const close = () => setVisible(false);
  /**
   * toggles the Popover
   */


  const toggle = () => setVisible(visible => !visible);
  /**
   * calls onOpen callback if provided
   *
   * @param {Object} event
   */


  const onOpenCallback = event => onOpen && onOpen({
    event
  });
  /**
   * calls onClose callback if provided
   *
   * @param {Object} event
   */


  const onCloseCallback = event => onClose && onClose({
    event
  });
  /**
   * toggles the popover
   *
   * this function is passed to the target element in `onClick`
   * also, calls `onOpen` or `onClose`, if provided, based on the state of the Popover
   * this function is also exposed to the user
   */


  const togglePopover = ({
    event
  } = {}) => {
    toggle();
    /**
     * setting the value in state is asynchronous
     * to counter this issue, the value of `open` is negated
     */

    !visible ? onOpenCallback(event) : onCloseCallback(event);
  };
  /**
   * closes the Popover
   * calls the `onCloseCallback` function
   * adds focus to the target element when closing non-programmatically
   */


  const closePopover = ({
    event
  } = {}) => {
    var _nodeToFocus;

    close();
    onCloseCallback(event);
    (_nodeToFocus = nodeToFocus) === null || _nodeToFocus === void 0 ? void 0 : _nodeToFocus.focus();
    nodeToFocus = null;
  };
  /**
   * opens the Popover
   * also calls the `onOpenCallback` function
   */


  const openPopover = ({
    event
  } = {}) => {
    open();
    onOpenCallback(event);
  };

  useEffect(() => {
    let timeout;

    if (visible && smartFocus) {
      /** setting timeout to prevent scroll to top if popover contains a focussable element
       * adding focus to elements after composition (after transform is applied on Popover)
       */
      timeout = setTimeout(() => {
        const {
          firstEl,
          total
        } = firstAndlastFocusEls(innerRef.current);
        /**
         * if there are any focusable elements, focus the first
         * else focus the whole popover to catch keydown events
         */

        if (total) {
          firstEl.focus();
        } else {
          innerRef.current.focus();
        }
      }, 0);
    }
    /**
     * fires `onClose` function if provided by the user
     *
     * if the target element is within the popover, does nothing
     * @param {Object} event
     */


    const outsideClick = event => {
      if (event.target === targetRef.current) return;
      const insidePopover = isTargetInsideRef(innerRef, event);

      if (!insidePopover) {
        closePopover({
          event
        });
      }
    };
    /**
     * handle keyDown events for Popover
     *
     * @param {Object} event
     */


    const onKeyDown = event => {
      const {
        ESC,
        TAB
      } = KEYCODES; // bail out if the pressed key in neither `ESC` or `TAB`

      if (!(event.keyCode === ESC || event.keyCode === TAB)) return; // Handle Esc keydown event

      if (event.keyCode === ESC) {
        closePopover({
          event
        });
      } // Handle Tab keydown event


      if (event.keyCode === TAB) {
        focusTrap(innerRef.current, event);
      }
    };

    if (visible) {
      document.addEventListener("keydown", onKeyDown);
      document.addEventListener("click", outsideClick);
      document.addEventListener("touchend", outsideClick);
    }

    return () => {
      document.removeEventListener("click", outsideClick);
      document.removeEventListener("touchend", outsideClick);
      document.removeEventListener("keydown", onKeyDown);
      clearTimeout(timeout);
    };
  }, [visible]);
  /**
   * adding `toggle`, `open` and `close` property in `ref.current` for user to interact with Popover
   */

  useImperativeHandle(ref, () => ({
    toggle: togglePopover,
    open: openPopover,
    close: closePopover
  }));
  /**
   * if ref is provided, then user has to handle the `onClick` functionality of the target element
   */

  const targetProps = ref ? {} : {
    onClick: event => {
      if (!visible) {
        nodeToFocus = event === null || event === void 0 ? void 0 : event.currentTarget;
      }

      togglePopover({
        event
      });
    }
  };
  return /*#__PURE__*/React.createElement(Popover$1, _extends({
    placement: placement,
    target: target,
    targetProps: targetProps,
    targetRef: targetRef,
    open: visible,
    skipBounds: skipBounds,
    ref: innerRef
  }, rest), children);
});
Popover.displayName = "Popover";
Popover.propTypes = {
  /**
   * React node to render inside Popover
   */
  children: PropTypes.node.isRequired,

  /**
   * Target node that triggers the Popover
   */
  target: PropTypes.element.isRequired,

  /**
   * Placement of the Popover with respect to target node
   *
   * Popover supports multiple placements:
   *
   * * `auto` • `auto-start` • `auto-end`
   * * `top` • `top-start` • `top-end`
   * * `bottom` • `bottom-start` • `bottom-end`
   * * `left` • `left-start` • `left-end`
   * * `right` • `right-start` • `right-end`
   */
  placement: PropTypes.oneOf(["auto", "auto-start", "auto-end", "top", "top-start", "top-end", "bottom", "bottom-start", "bottom-end", "right", "right-start", "right-end", "left", "left-start", "left-end"]),

  /**
   * Adds focus to first interactive element when Popover is visible.
   * It's enabled by default for better accessibility
   *
   * > from v1.3.0+
   */
  smartFocus: PropTypes.bool,

  /**
   * When Popover is added to a container with `position: fixed` and/or `overflow: hidden`,
   * it creates a containing block with a stacking context which the Popover can't skip.
   * In such senarios, enabling `skipBounds` will force Popover to skip its parent's boundaries.
   *
   * > from v1.3.0+
   */
  skipBounds: PropTypes.bool,

  /**
   * Callback to notify that Popover is open.
   * The callback has the argument of the shape:
   *
   * ```
   * {
   *   event: React synthetic event
   * }
   * ```
   */
  onOpen: PropTypes.func,

  /**
   * Callback to notify that Popover is close.
   * The callback has the argument of the shape:
   *
   * ```
   * {
   *   event: React synthetic event
   * }
   * ```
   */
  onClose: PropTypes.func
};
Popover.defaultProps = {
  placement: "bottom",
  smartFocus: true,
  skipBounds: false
};

/**
 * calls `open` function
 *
 * exposed to the user to open the Popover
 * @param {Object|function} ref
 */
const open = ref => {
  if (!(ref && ref.current && ref.current.open)) {
    console.warn(`you need to pass the same ref which you passed in "Popover"`);
    return;
  }

  ref.current.open();
};

/**
 * calls `close` function
 *
 * exposed to the user to close the Popover
 * @param {Object|function} ref
 */
const close = ref => {
  if (!(ref && ref.current && ref.current.close)) {
    console.warn(`you need to pass the same ref which you passed in "Popover"`);
    return;
  }

  ref.current.close();
};

/**
 * calls `toggle` function
 *
 * exposed to the user to toggle the Popover
 * @param {Object|function} ref
 */
const toggle = ref => {
  //changing optional chaining expression to logical AND in favor of updated react-docgen version fails to parse components with optional chaining
  if (!(ref && ref.current && ref.current.toggle)) {
    console.warn(`you need to pass the same ref which you passed in "Popover"`);
    return;
  }

  ref.current.toggle();
};

export { close, Popover as default, open, toggle };
